import { useEffect, useState } from "react";
import { Disc3, Link2, Trash2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "../lib/api";
import { FadeUp } from "../components/Reveal";

const artSrc = (url) => (url?.startsWith("/api/") ? `${process.env.REACT_APP_BACKEND_URL}${url}` : url);

function ReleaseForm({ onAdded }) {
  const [url, setUrl] = useState("");
  const [meta, setMeta] = useState(null);
  const [form, setForm] = useState({ title: "", artist: "", year: new Date().getFullYear() });
  const [busy, setBusy] = useState(false);

  const preview = async () => {
    if (!url.trim()) return;
    setBusy(true);
    try {
      const r = await api.post("/admin/releases/preview", { stream_url: url });
      setMeta(r.data);
      setForm((f) => ({ ...f, title: r.data.title || f.title, artist: r.data.artist || f.artist }));
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/releases", { stream_url: url, title: form.title, artist: form.artist, year: Number(form.year) });
      toast.success("Release posted");
      setUrl(""); setMeta(null); setForm({ title: "", artist: "", year: new Date().getFullYear() });
      onAdded();
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setBusy(false); }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-3xl border border-purple-100 p-7 space-y-4" data-testid="release-form">
      <p className="font-serif text-2xl text-obsidian flex items-center gap-2"><Sparkles size={18} className="text-gold" /> Post a release</p>
      <div className="flex flex-wrap gap-3">
        <input value={url} onChange={(e) => setUrl(e.target.value)} onBlur={preview} placeholder="Paste a YouTube or Spotify link" required data-testid="release-url-input"
          className="flex-1 min-w-[260px] rounded-xl border border-purple-100 px-3 py-2.5 text-sm" />
        <button type="button" onClick={preview} disabled={busy} data-testid="release-fetch-btn" className="flex items-center gap-1.5 px-4 py-2.5 rounded-full bg-lilac-mist text-obsidian text-xs hover:bg-purple-100 transition-colors">
          <Link2 size={13} /> Fetch details
        </button>
      </div>
      {meta && (
        <div className="flex gap-4 items-center p-4 rounded-2xl bg-lilac-mist" data-testid="release-preview">
          {meta.thumb && <img src={meta.thumb} alt="" className="w-20 h-20 rounded-xl object-cover" />}
          <div className="flex-1 grid sm:grid-cols-3 gap-3">
            <input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Title" required data-testid="release-title-input" className="rounded-xl border border-purple-100 px-3 py-2 text-sm" />
            <input value={form.artist} onChange={(e) => setForm({ ...form, artist: e.target.value })} placeholder="Artist" required data-testid="release-artist-input" className="rounded-xl border border-purple-100 px-3 py-2 text-sm" />
            <input type="number" value={form.year} onChange={(e) => setForm({ ...form, year: e.target.value })} placeholder="Year" data-testid="release-year-input" className="rounded-xl border border-purple-100 px-3 py-2 text-sm" />
          </div>
        </div>
      )}
      <button type="submit" disabled={busy || !url} data-testid="release-submit" className="px-6 py-2.5 rounded-full bg-gold text-obsidian text-xs font-semibold hover:brightness-110 transition-all disabled:opacity-50">
        {busy ? "Working…" : "Add to Releases"}
      </button>
    </form>
  );
}

export default function AdminReleases() {
  const [releases, setReleases] = useState([]);
  const load = () => api.get("/releases").then((r) => setReleases(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const remove = async (r) => {
    if (!window.confirm(`Remove "${r.title}" from the site?`)) return;
    try { await api.delete(`/admin/releases/${r.release_id}`); toast.success("Removed"); load(); }
    catch (err) { toast.error(formatApiError(err)); }
  };

  return (
    <div data-testid="admin-releases">
      <FadeUp>
        <h1 className="font-serif text-3xl sm:text-4xl text-obsidian">Featured <span className="italic text-purple-700">releases</span></h1>
        <p className="text-sm text-slate-500 mt-2 flex items-center gap-2"><Disc3 size={14} className="text-gold" /> Paste a link — title, artist and artwork are pulled automatically. Newest shows first on the public site.</p>
      </FadeUp>
      <div className="mt-10"><ReleaseForm onAdded={load} /></div>
      <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-3" data-testid="release-list">
        {releases.map((r) => (
          <div key={r.release_id} className="bg-white rounded-3xl border border-purple-100 overflow-hidden group" data-testid={`release-card-${r.release_id}`}>
            <img src={artSrc(r.artwork_url)} alt={r.title} className="w-full aspect-square object-cover" />
            <div className="p-4 flex items-start justify-between gap-3">
              <div>
                <p className="font-medium text-obsidian">{r.title}</p>
                <p className="text-xs text-slate-400">{r.artist} · {r.platform} · {r.year}</p>
              </div>
              <button onClick={() => remove(r)} data-testid={`release-delete-${r.release_id}`} className="p-2 rounded-full text-slate-400 hover:text-red-600 hover:bg-red-50 transition-colors"><Trash2 size={14} /></button>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
